import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  Box, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, InputRightElement, Spinner, Text, useColorMode,
} from '@chakra-ui/react';
import {
  LuBanknote, LuCheck, LuCircleX, LuCreditCard, LuLandmark, LuPlus, LuSearch,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { createTag, fetchAccountFreshness, fetchTags } from '../../api/endpointsBackend';
import { getWorkspaceId } from '../../auth/workspaceService';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { CATEGORY_ORDER, getDisplayCategory } from '../../utils/accountTypes';
import { categoryTint } from '../../utils/categoryTint';
import { todayLocalISO } from '../../utils/localDate';
import PhoneSheet from './PhoneSheet';

// ---------------------------------------------------------------------------
// "Imports by itself"
// ---------------------------------------------------------------------------

// One freshness read per workspace per session: which accounts own a statement.
const freshnessCache = { workspaceId: null, promise: null };

function loadStatementAccountIds() {
  const workspaceId = String(getWorkspaceId() ?? '');
  if (freshnessCache.promise && freshnessCache.workspaceId === workspaceId) return freshnessCache.promise;
  const promise = fetchAccountFreshness()
    .then((data) => new Set((data?.accounts || []).map((row) => row.account_id)))
    .catch(() => {
      if (freshnessCache.promise === promise) freshnessCache.promise = null;
      return new Set();
    });
  freshnessCache.workspaceId = workspaceId;
  freshnessCache.promise = promise;
  return promise;
}

/**
 * Whether an account "imports by itself": its movements arrive from its bank,
 * so one logged by hand may end up twice. No API says so, so it is derived:
 * not cash, and it owns a statement file or has imported rows
 * (`has_transactions`). Returns a predicate over accounts.
 * `enabled: false` (a closed sheet) defers the read.
 */
export function useImportsByItself(enabled = true) {
  const { can } = useWorkspace();
  const canRead = can('source_file.read');
  const [statementIds, setStatementIds] = useState(() => new Set());

  useEffect(() => {
    if (!canRead || !enabled) return undefined;
    let alive = true;
    loadStatementAccountIds().then((ids) => { if (alive) setStatementIds(ids); });
    return () => { alive = false; };
  }, [canRead, enabled]);

  return useMemo(() => (account) => Boolean(
    account
      && account.account_type !== 'cash'
      && (statementIds.has(account.id) || account.has_transactions),
  ), [statementIds]);
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function Caption({ children, trailing, mt = '16px' }) {
  return (
    <Flex align="baseline" justify="space-between" px="16px" mt={mt} mb="8px" gap="8px">
      <Text textStyle="phoneCaption" color="phone.muted">{children}</Text>
      {trailing}
    </Flex>
  );
}

/** A group of rows on one card, divided by hairlines. */
export function RowCard({ children, mx = '16px' }) {
  return (
    <Box
      mx={mx}
      borderRadius="14px"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      overflow="hidden"
      sx={{ '& > * + *': { borderTop: '1px solid', borderColor: 'redesign.border' } }}
    >
      {children}
    </Box>
  );
}

/** A 52px (or taller) row: leading icon, a title and an optional second line, a trailing mark. */
export function PickerRow({
  leading, title, subtitle, trailing, onClick, selected, color, minH = '52px', titleLines = 1,
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns={leading ? '28px minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto'}
      gap="12px"
      alignItems="center"
      w="100%"
      minH={minH}
      px="14px"
      py="6px"
      textAlign="left"
      aria-pressed={selected === undefined ? undefined : selected}
      _active={{ bg: 'redesign.bg.hover' }}
    >
      {leading && <Flex justify="center" color="phone.muted">{leading}</Flex>}
      <Box minW={0}>
        <Text textStyle="phoneBody" color={color || 'redesign.heading'} noOfLines={titleLines || undefined} overflowWrap={titleLines === 1 ? undefined : 'anywhere'}>{title}</Text>
        {subtitle && <Box textStyle="phoneMeta" color="phone.muted" mt="1px" minW={0}>{subtitle}</Box>}
      </Box>
      <Flex align="center" color="phone.link">
        {trailing !== undefined ? trailing : (selected && <Box as={LuCheck} boxSize="20px" />)}
      </Flex>
    </Box>
  );
}

/** The dashed empty circle a "None" row leads with. */
function NoneDisc() {
  return <Box w="24px" h="24px" borderRadius="full" border="1.5px dashed" borderColor="phone.muted" />;
}

/** A category's emoji on a disc tinted with its colour. */
export function CategoryDisc({ category, size = 40 }) {
  const { colorMode } = useColorMode();
  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      align="center"
      justify="center"
      flexShrink={0}
      bg={category?.color ? categoryTint(category.color, colorMode).bg : 'redesign.bg.hover'}
    >
      <Box as="span" fontSize={`${Math.round(size * 0.48)}px`} lineHeight="1">
        {category?.icon || category?.name?.charAt(0)}
      </Box>
    </Flex>
  );
}

const NARROW = '@media screen and (max-width: 359.98px)';
// A sheet wider than a phone held upright (a landscape phone) lays out more
// tiles per row, so the detent shows more than a row and a half.
const WIDE_SHEET = '@media screen and (min-width: 600px)';

/**
 * A category tile: the disc over the name, 76px (the picker's), or `plain`,
 * the emoji alone over the name, 72px (quick add's). `mark` sits in its top
 * right corner (quick add's ✨ for a value a description filled).
 *
 * A name wraps between words only. A word too long for the tile is hyphenated
 * where the browser can, else ends in an ellipsis; it is never split with no
 * mark.
 */
export function CategoryTile({
  category, selected, onClick, mark, plain = false,
}) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap="5px"
      minH={plain ? '72px' : '76px'}
      minW={0}
      px="2px"
      py="6px"
      borderRadius="14px"
      bg={selected ? 'redesign.bg.accent' : 'redesign.bg.card'}
      border="1px solid"
      borderColor={selected ? 'phone.link' : 'redesign.border'}
      // The second pixel of the chosen border, drawn inside so the tile does
      // not move.
      boxShadow={selected ? 'inset 0 0 0 1px var(--chakra-colors-phone-link)' : undefined}
      color={selected ? 'redesign.heading' : 'redesign.text'}
      aria-pressed={selected}
      _active={{ bg: 'redesign.bg.hover' }}
    >
      {plain ? (
        <Box as="span" fontSize="20px" lineHeight="1">{category?.icon || category?.name?.charAt(0)}</Box>
      ) : (
        <CategoryDisc category={category} />
      )}
      <Box
        as="span"
        w="100%"
        fontSize="11px"
        fontWeight="500"
        lineHeight="13px"
        textAlign="center"
        noOfLines={2}
        sx={{
          overflowWrap: 'normal',
          wordBreak: 'normal',
          hyphens: 'auto',
          WebkitHyphens: 'auto',
          [NARROW]: { fontSize: '10px', lineHeight: '12px' },
        }}
      >
        {category.name.split(' ').map((word, i) => (
          <React.Fragment key={`${word}-${i}`}>
            {i > 0 && ' '}
            {/* Each word clips on its own, so a long one ends in "…"
                instead of running under the tile's edge. */}
            <Box as="span" display="inline-block" maxW="100%" overflow="hidden" textOverflow="ellipsis" verticalAlign="top" overflowWrap="normal">
              {word}
            </Box>
          </React.Fragment>
        ))}
      </Box>
      {mark && <Box position="absolute" top="3px" right="5px">{mark}</Box>}
    </Box>
  );
}

export function TileGrid({ children }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(4, minmax(0, 1fr))"
      sx={{ [WIDE_SHEET]: { gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))' } }}
      gap="8px"
      px="16px"
    >
      {children}
    </Box>
  );
}

/** A 44px, 16px search field. Never focused on its own: the user taps it. */
const SearchField = React.forwardRef(function SearchField({ value, onChange, placeholder }, ref) {
  const { t } = useTranslation('movements');
  return (
    <InputGroup px="16px" mt="4px" mb="8px">
      <InputLeftElement pointerEvents="none" h="44px" ml="16px" color="phone.muted">
        <Box as={LuSearch} boxSize="18px" />
      </InputLeftElement>
      <Input
        ref={ref}
        type="search"
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        h="44px"
        borderRadius="12px"
        bg="redesign.bg.card"
        borderColor="redesign.border"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        pr="44px"
        // Its own clear button is the 44px one beside it.
        sx={{ '&::-webkit-search-cancel-button': { display: 'none' } }}
      />
      {value && (
        <InputRightElement h="44px" w="44px" mr="16px">
          <IconButton
            icon={<Box as={LuCircleX} boxSize="18px" />}
            variant="ghost"
            color="phone.muted"
            // The theme's 44px minimum stops at the md breakpoint, which a
            // landscape phone passes.
            minW="44px"
            minH="44px"
            aria-label={t('pickers.clearSearch')}
            onClick={() => onChange('')}
          />
        </InputRightElement>
      )}
    </InputGroup>
  );
});

const matches = (text, query) => String(text || '').toLowerCase().includes(query.trim().toLowerCase());

// ---------------------------------------------------------------------------
// CategorySheet
// ---------------------------------------------------------------------------

/**
 * Pick a category of the movement's type: the most used first (`recentIds`),
 * then every one as 4-column tiles, then "None". The search appears only when
 * the 🔍 is tapped, and is focused inside that tap so iOS raises the keyboard.
 *
 * "None" clears a category, so it shows only when there is one to clear, and
 * `allowNone={false}` leaves it out.
 */
export function CategorySheet({
  isOpen, onClose, categories = [], type, value, onChange, recentIds = [], allowNone = true,
}) {
  const { t } = useTranslation('movements');
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    if (!isOpen) { setSearching(false); setQuery(''); }
  }, [isOpen]);

  const ofType = useMemo(() => categories.filter((c) => c.type === type), [categories, type]);
  const recent = useMemo(
    () => recentIds.map((id) => ofType.find((c) => c.id === id)).filter(Boolean).slice(0, 4),
    [recentIds, ofType],
  );
  const rest = useMemo(() => {
    const recentSet = new Set(recent.map((c) => c.id));
    return ofType.filter((c) => !recentSet.has(c.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [ofType, recent]);
  const found = query.trim() ? ofType.filter((c) => matches(c.name, query)) : null;

  const pick = (category) => {
    onChange(category);
    onClose();
  };

  const startSearch = () => {
    flushSync(() => setSearching(true));
    searchRef.current?.focus();
  };

  const tiles = (list) => (
    <TileGrid>
      {list.map((c) => (
        <CategoryTile key={c.id} category={c} selected={c.id === value} onClick={() => pick(c)} />
      ))}
    </TileGrid>
  );

  return (
    <PhoneSheet
      id="picker-category"
      isOpen={isOpen}
      onClose={onClose}
      size="detent"
      title={t('pickers.category.title')}
      headerRight={searching ? null : (
        <IconButton
          icon={<Box as={LuSearch} boxSize="22px" />}
          variant="ghost"
          color="redesign.text"
          minW="44px"
          minH="44px"
          aria-label={t('pickers.category.search')}
          onClick={startSearch}
        />
      )}
    >
      {searching && (
        <SearchField ref={searchRef} value={query} onChange={setQuery} placeholder={t('pickers.category.search')} />
      )}
      {found ? (
        <Box pt="4px">
          {found.length ? tiles(found) : (
            <Text textStyle="phoneBody" color="phone.muted" px="16px" py="12px">{t('pickers.noMatches')}</Text>
          )}
        </Box>
      ) : (
        <>
          {recent.length > 0 && (
            <>
              <Caption mt="4px">{t('pickers.category.mostUsed')}</Caption>
              {tiles(recent)}
            </>
          )}
          <Caption mt={recent.length ? '16px' : '4px'}>
            {t(type === 'Income' ? 'pickers.category.income' : 'pickers.category.expenses')}
          </Caption>
          {tiles(rest)}
        </>
      )}
      {allowNone && value != null && (
        <Box mt="16px">
          <RowCard>
            <PickerRow leading={<NoneDisc />} title={t('pickers.none')} onClick={() => pick(null)} />
          </RowCard>
        </Box>
      )}
    </PhoneSheet>
  );
}

// ---------------------------------------------------------------------------
// AccountSheet
// ---------------------------------------------------------------------------

const SEARCH_FROM = 12;

export function AccountIcon({ account, size = 20 }) {
  const icon = account?.account_type === 'cash' ? LuBanknote
    : account?.account_type === 'credit' ? LuCreditCard
      : LuLandmark;
  return <Box as={icon} boxSize={`${size}px`} />;
}

/** An account's icon on a 28px disc, as the picker's rows lead with it. */
function AccountDisc({ account }) {
  return (
    <Flex w="28px" h="28px" borderRadius="full" align="center" justify="center" bg="phone.chipBg" color="redesign.text">
      <AccountIcon account={account} size={16} />
    </Flex>
  );
}

/** The small "Imports by itself" pill on an account row. */
export function ImportsTag() {
  const { t } = useTranslation('movements');
  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      h="20px"
      px="8px"
      ml="8px"
      borderRadius="10px"
      bg="phone.chipBg"
      color="redesign.text"
      fontSize="11px"
      lineHeight="20px"
      fontWeight="600"
      whiteSpace="nowrap"
    >
      {t('pickers.account.importsByItself')}
    </Box>
  );
}

/**
 * Pick an account: active accounts, the recently used first, then in the
 * Accounts page's groups and order, each with its currencies and the
 * "Imports by itself" tag. A search field appears from 12 accounts. The sheet
 * is as tall as its list, up to the full height, where it scrolls.
 */
export function AccountSheet({
  isOpen, onClose, accounts = [], value, onChange, recentIds = [], title, allowNone = true,
}) {
  const { t } = useTranslation(['movements', 'accounts']);
  const importsByItself = useImportsByItself();
  const [query, setQuery] = useState('');

  useEffect(() => { if (!isOpen) setQuery(''); }, [isOpen]);

  const active = useMemo(
    () => accounts.filter((a) => a.is_active !== false || a.id === value),
    [accounts, value],
  );
  const recent = useMemo(
    () => recentIds.map((id) => active.find((a) => a.id === id)).filter(Boolean).slice(0, 3),
    [recentIds, active],
  );
  const groups = useMemo(() => {
    const recentSet = new Set(recent.map((a) => a.id));
    const byGroup = new Map();
    active.filter((a) => !recentSet.has(a.id)).forEach((a) => {
      const key = getDisplayCategory(a);
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(a);
    });
    const order = (key) => (CATEGORY_ORDER.includes(key) ? CATEGORY_ORDER.indexOf(key) : CATEGORY_ORDER.length);
    return [...byGroup.entries()].sort(([a], [b]) => order(a) - order(b) || a.localeCompare(b));
  }, [active, recent]);

  const pick = (account) => {
    onChange(account);
    onClose();
  };

  const row = (a) => (
    <PickerRow
      key={a.id}
      minH="56px"
      leading={<AccountDisc account={a} />}
      title={a.name}
      subtitle={(
        <Flex align="center" minW={0}>
          <Box as="span" noOfLines={1}>{(a.currencies || []).join(' · ')}</Box>
          {importsByItself(a) && <ImportsTag />}
        </Flex>
      )}
      selected={a.id === value}
      onClick={() => pick(a)}
    />
  );

  const found = query.trim() ? active.filter((a) => matches(a.name, query)) : null;

  return (
    <PhoneSheet
      id="picker-account"
      isOpen={isOpen}
      onClose={onClose}
      size="content"
      title={title || t('pickers.account.title')}
    >
      {active.length >= SEARCH_FROM && (
        <SearchField value={query} onChange={setQuery} placeholder={t('pickers.account.search')} />
      )}
      {found ? (
        found.length ? <RowCard>{found.map(row)}</RowCard> : (
          <Text textStyle="phoneBody" color="phone.muted" px="16px" py="12px">{t('pickers.noMatches')}</Text>
        )
      ) : (
        <>
          {recent.length > 0 && (
            <>
              <Caption mt="4px">{t('pickers.account.recent')}</Caption>
              <RowCard>{recent.map(row)}</RowCard>
            </>
          )}
          {groups.map(([type, list], i) => (
            <Box key={type}>
              <Caption mt={i === 0 && recent.length === 0 ? '4px' : '16px'}>
                {t(`accounts:categories.${type}`, { defaultValue: type })}
              </Caption>
              <RowCard>{list.map(row)}</RowCard>
            </Box>
          ))}
        </>
      )}
      {allowNone && (
        <Box mt="16px">
          <RowCard>
            <PickerRow leading={<NoneDisc />} title={t('pickers.none')} selected={value == null} onClick={() => pick(null)} />
          </RowCard>
        </Box>
      )}
    </PhoneSheet>
  );
}

// ---------------------------------------------------------------------------
// TagSheet
// ---------------------------------------------------------------------------

/** A 22px box: filled, with a white check, when the tag is chosen. */
function TagCheck({ on }) {
  return (
    <Flex
      w="22px"
      h="22px"
      borderRadius="6px"
      align="center"
      justify="center"
      bg={on ? 'redesign.accent.fill' : 'transparent'}
      border="1.5px solid"
      borderColor={on ? 'redesign.accent.fill' : 'phone.muted'}
      color="white"
    >
      {on && <Box as={LuCheck} boxSize="15px" strokeWidth={3} />}
    </Flex>
  );
}

/** `text` with the part that matches `query` in bold. */
function Highlight({ text, query }) {
  const at = query ? text.toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (at < 0) return text;
  return (
    <>
      {text.slice(0, at)}
      <Box as="b" fontWeight="700">{text.slice(at, at + query.length)}</Box>
      {text.slice(at + query.length)}
    </>
  );
}

/**
 * Pick several tags. Changes apply on Done; dragging the sheet down or the
 * back swipe drops them. With `tag.create`, a search that names no tag offers
 * "+ Create tag “…”", a name-only create; a name that already exists (409)
 * selects that tag instead.
 *
 * `onChange(ids, tags)` gets the chosen ids and the tag objects, created ones
 * included.
 */
export function TagSheet({ isOpen, onClose, tags = [], value = [], onChange }) {
  const { t } = useTranslation('movements');
  const { can } = useWorkspace();
  const [selected, setSelected] = useState(value);
  const [created, setCreated] = useState([]);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelected(value);
      setQuery('');
      setCreateError(null);
    }
    // Only when it opens: while open the selection is the sheet's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // An archived tag stays listed while it is on the movement, or once it was
  // picked here (a create that met an archived tag of that name selects it).
  const all = useMemo(() => {
    const byId = new Map();
    [...tags, ...created].forEach((tag) => byId.set(tag.id, tag));
    const createdIds = new Set(created.map((tag) => tag.id));
    return [...byId.values()].filter((tag) => (
      tag.is_active !== false || value.includes(tag.id) || createdIds.has(tag.id)
    ));
  }, [tags, created, value]);

  const q = query.trim();
  const list = useMemo(() => {
    const shown = q ? all.filter((tag) => matches(tag.name, q)) : all;
    // What is already chosen first, so a long list does not hide it.
    return [...shown].sort((a, b) => (
      Number(value.includes(b.id)) - Number(value.includes(a.id)) || a.name.localeCompare(b.name)
    ));
  }, [all, q, value]);
  // While searching, the chosen tags the search hides stay in view below it.
  const chosenHidden = q ? all.filter((tag) => selected.includes(tag.id) && !list.includes(tag)) : [];
  const exact = all.some((tag) => tag.name.toLowerCase() === q.toLowerCase());

  const toggle = (id) => setSelected((prev) => (
    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
  ));

  const create = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const tag = await createTag({ name: q });
      setCreated((prev) => [...prev, tag]);
      setSelected((prev) => [...prev, tag.id]);
      setQuery('');
    } catch (err) {
      if (err.response?.status === 409) {
        const existing = (await fetchTags().catch(() => []))
          .find((tag) => tag.name.toLowerCase() === q.toLowerCase());
        if (existing) {
          setCreated((prev) => [...prev, existing]);
          setSelected((prev) => (prev.includes(existing.id) ? prev : [...prev, existing.id]));
          setQuery('');
          return;
        }
      }
      setCreateError(t('pickers.tags.createError'));
    } finally {
      setCreating(false);
    }
  };

  const done = () => {
    const byId = new Map(all.map((tag) => [tag.id, tag]));
    onChange(selected, selected.map((id) => byId.get(id)).filter(Boolean));
    onClose();
  };

  const tagRow = (tag) => {
    const on = selected.includes(tag.id);
    return (
      <PickerRow
        key={tag.id}
        leading={<TagCheck on={on} />}
        title={<>#<Highlight text={tag.name} query={q} /></>}
        trailing={null}
        selected={on}
        onClick={() => toggle(tag.id)}
      />
    );
  };

  return (
    <PhoneSheet
      id="picker-tags"
      isOpen={isOpen}
      onClose={onClose}
      // A short list gets a sheet as tall as it is; a long one the detent.
      size={all.length > 8 ? 'detent' : 'content'}
      title={t('pickers.tags.title')}
      headerRight={(
        <Button variant="ghost" color="phone.link" fontSize="17px" fontWeight="600" px="12px" minW="44px" minH="44px" onClick={done}>
          {t('pickers.done')}
        </Button>
      )}
    >
      <SearchField value={query} onChange={setQuery} placeholder={t('pickers.tags.search')} />
      {(list.length > 0 || (q && !exact && can('tag.create'))) ? (
        <RowCard>
          {list.map(tagRow)}
          {q && !exact && can('tag.create') && (
            <PickerRow
              leading={creating ? <Spinner size="sm" /> : <Box as={LuPlus} boxSize="20px" color="phone.link" />}
              title={t('pickers.tags.create', { name: q })}
              color="phone.link"
              trailing={null}
              onClick={creating ? undefined : create}
            />
          )}
        </RowCard>
      ) : (
        <Text textStyle="phoneBody" color="phone.muted" px="16px" py="12px">
          {q ? t('pickers.noMatches') : t('pickers.tags.empty')}
        </Text>
      )}
      {createError && (
        <Text textStyle="phoneMeta" color="phone.expense" px="16px" mt="8px">{createError}</Text>
      )}
      {chosenHidden.length > 0 && (
        <>
          <Caption>{t('pickers.tags.selected')}</Caption>
          <RowCard>{chosenHidden.map(tagRow)}</RowCard>
        </>
      )}
    </PhoneSheet>
  );
}

// ---------------------------------------------------------------------------
// CurrencySheet
// ---------------------------------------------------------------------------

/** Pick a currency: the code and the backend's own description of it. */
export function CurrencySheet({ isOpen, onClose, currencies = [], value, onChange }) {
  const { t } = useTranslation('movements');
  const pick = (code) => {
    onChange(code);
    onClose();
  };
  return (
    <PhoneSheet
      id="picker-currency"
      isOpen={isOpen}
      onClose={onClose}
      size={currencies.length > 6 ? 'detent' : 'content'}
      title={t('pickers.currency.title')}
    >
      <Box pt="4px">
        <RowCard>
          {currencies.filter((c) => c.is_active !== false).map((c) => (
            <PickerRow
              key={c.name}
              title={c.description ? `${c.name} — ${c.description}` : c.name}
              selected={c.name === value}
              onClick={() => pick(c.name)}
            />
          ))}
        </RowCard>
      </Box>
    </PhoneSheet>
  );
}

// ---------------------------------------------------------------------------
// SourceSheet
// ---------------------------------------------------------------------------

/**
 * Pick an account's institution from the catalog, or create one by the name
 * searched for. `glyph(source)` draws its mark (it lives with the account
 * form). The search is shown from the start but never focused on open.
 */
export function SourceSheet({
  isOpen, onClose, sources = [], value, onChange, onCreate, glyph,
}) {
  const { t } = useTranslation('accounts');
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => { if (!isOpen) setQuery(''); }, [isOpen]);

  const q = query.trim();
  const list = q ? sources.filter((s) => matches(s.name, q)) : sources;
  const exact = sources.some((s) => s.name.toLowerCase() === q.toLowerCase());

  const pick = (source) => {
    onChange(source);
    onClose();
  };

  const create = async () => {
    setCreating(true);
    try {
      await onCreate(q);
      onClose();
    } finally {
      setCreating(false);
    }
  };

  return (
    <PhoneSheet
      id="picker-source"
      isOpen={isOpen}
      onClose={onClose}
      size="detent"
      title={t('detail.fields.source')}
    >
      <SearchField value={query} onChange={setQuery} placeholder={t('detail.fields.sourceSearch')} />
      {(list.length > 0 || (q && !exact)) ? (
        <RowCard>
          {list.map((s) => (
            <PickerRow
              key={s.id}
              leading={glyph?.(s)}
              title={<Highlight text={s.name} query={q} />}
              selected={s.id === value}
              onClick={() => pick(s)}
            />
          ))}
          {q && !exact && (
            <PickerRow
              leading={creating ? <Spinner size="sm" /> : <Box as={LuPlus} boxSize="20px" color="phone.link" />}
              title={t('detail.fields.sourceCreate', { name: q })}
              // Unclamped: this row is the only place the name about to be
              // created shows whole once the search field scrolls it.
              titleLines={null}
              color="phone.link"
              trailing={null}
              onClick={creating ? undefined : create}
            />
          )}
        </RowCard>
      ) : (
        <Text textStyle="phoneBody" color="phone.muted" px="16px" py="12px">
          {t('detail.fields.sourceEmpty')}
        </Text>
      )}
      {value != null && !q && (
        <Box mt="16px">
          <RowCard>
            <PickerRow leading={<NoneDisc />} title={t('detail.fields.sourceNone')} onClick={() => pick(null)} />
          </RowCard>
        </Box>
      )}
    </PhoneSheet>
  );
}

// ---------------------------------------------------------------------------
// DateSheet
// ---------------------------------------------------------------------------

/** "Sun, Sep 27" in the user's language, US order. */
export function shortDayLabel(iso, t) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const days = t('dates:days.short', { returnObjects: true });
  const months = t('dates:months.short', { returnObjects: true });
  return `${days[date.getDay()]}, ${months[m - 1]} ${d}`;
}

/** Pick a date: the native date input, or a chip for one of the last 7 days. */
export function DateSheet({ isOpen, onClose, value, onChange }) {
  const { t } = useTranslation(['movements', 'dates']);
  const days = useMemo(() => {
    const out = [];
    const now = new Date();
    for (let i = 0; i < 7; i += 1) {
      out.push(todayLocalISO(new Date(now.getFullYear(), now.getMonth(), now.getDate() - i)));
    }
    return out;
    // Re-read "today" each time it opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const pick = (iso) => {
    if (!iso) return;
    onChange(iso);
    onClose();
  };

  return (
    <PhoneSheet id="picker-date" isOpen={isOpen} onClose={onClose} size="content" title={t('pickers.date.title')}>
      <Box px="16px" pt="4px" pb="12px">
        <Input
          type="date"
          h="52px"
          borderRadius="12px"
          bg="redesign.bg.card"
          borderColor="redesign.border"
          value={value || ''}
          aria-label={t('pickers.date.other')}
          // iOS reports each turn of the wheel: set the date, but leave the
          // sheet open until the user is done with it.
          onChange={(e) => { if (e.target.value) onChange(e.target.value); }}
        />
      </Box>
      <Flex flexWrap="wrap" gap="8px" px="16px" pb="8px">
        {days.map((iso, i) => {
          const on = iso === value;
          return (
            <Box
              key={iso}
              as="button"
              type="button"
              onClick={() => pick(iso)}
              display="inline-flex"
              alignItems="center"
              gap="6px"
              h="44px"
              px="14px"
              borderRadius="22px"
              bg={on ? 'redesign.bg.accent' : 'redesign.bg.card'}
              border="1px solid"
              borderColor={on ? 'phone.link' : 'redesign.border'}
              color="redesign.heading"
              fontSize="15px"
              whiteSpace="nowrap"
              aria-pressed={on}
              _active={{ bg: 'redesign.bg.hover' }}
            >
              {i === 0 ? t('quickAdd.dates.today') : i === 1 ? t('quickAdd.dates.yesterday') : shortDayLabel(iso, t)}
              {on && <Box as={LuCheck} boxSize="15px" color="phone.link" />}
            </Box>
          );
        })}
      </Flex>
    </PhoneSheet>
  );
}
